import { Capacitor } from "@capacitor/core";
import { Keyboard } from "@capacitor/keyboard";
import { StatusBar, Style } from "@capacitor/status-bar";

export class StatusBarService {
  private static instance: StatusBarService;

  private constructor() {
    // Listen for theme changes
    this.setupThemeListener();
    // Setup keyboard listeners
    this.setupKeyboardListeners();
  }

  static getInstance(): StatusBarService {
    if (!StatusBarService.instance) {
      StatusBarService.instance = new StatusBarService();
    }
    return StatusBarService.instance;
  }

  private setupThemeListener(): void {
    // Listen for theme changes in the DOM
    const observer = new MutationObserver(() => {
      this.updateStatusBarStyle();
    });

    // Watch for class changes on html element
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
  }

  private setupKeyboardListeners(): void {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const _platform = Capacitor.getPlatform();

    Keyboard.addListener("keyboardDidShow", (info) => {
      // Get current safe area value
      const safeAreaBottom = Number.parseInt(
        getComputedStyle(document.documentElement).getPropertyValue("--safe-area-inset-bottom") ||
          "0",
        10
      );

      // On both platforms, keyboard height includes safe area overlap, so subtract it
      const keyboardHeight = Math.max(0, info.keyboardHeight - safeAreaBottom);

      document.documentElement.style.setProperty("--keyboard-height", `${keyboardHeight}px`);

      // Scroll focused element into view after sheet resizes
      setTimeout(() => {
        const activeElement = document.activeElement as HTMLElement;
        if (activeElement && activeElement !== document.body) {
          activeElement.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
    });

    Keyboard.addListener("keyboardDidHide", () => {
      document.documentElement.style.setProperty("--keyboard-height", "0px");
    });
  }

  private updateStatusBarStyle(): void {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const isDark = document.documentElement.classList.contains("dark");
    const platform = Capacitor.getPlatform();

    if (platform === "android") {
      // For Android: Use native interface (StatusBar plugin doesn't work in edge-to-edge mode)
      try {
        (window as any).AndroidTheme?.setStatusBarTheme(isDark);
      } catch (err) {
        console.error("Failed to update Android status bar:", err);
      }
    } else if (platform === "ios") {
      // For iOS: Only set style (text/icon color)
      // Style.Light = dark text for light backgrounds (dark icons)
      // Style.Dark = light text for dark backgrounds (light icons)
      // Note: We use overlaysWebView mode, so the status bar is transparent
      // and shows the app's background color underneath
      StatusBar.setStyle({ style: isDark ? Style.Dark : Style.Light }).catch((err) => {
        console.error("Failed to update iOS status bar style:", err);
      });
    }
  }

  async initialize(): Promise<void> {
    // Only run on native platforms
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    try {
      const platform = Capacitor.getPlatform();

      if (platform === "android") {
        await this.initializeAndroid();
      } else if (platform === "ios") {
        await this.initializeIOS();
      }

      // Set initial status bar style based on current theme
      this.updateStatusBarStyle();
    } catch (error) {
      console.error("❌ Error configuring status bar:", error);
    }
  }

  private async initializeAndroid(): Promise<void> {
    // Wait a moment for the layout to settle
    await new Promise((resolve) => setTimeout(resolve, 100));

    // Try to get actual measurements
    const _densityRatio = window.devicePixelRatio;

    // Standard Android status bar is 24dp
    const statusBarHeightDp = 24;

    // Navigation bar / gesture area at bottom
    // Modern devices: gesture area is ~16dp
    // Older devices with buttons: ~48dp
    // We'll use 16dp as a safe default for modern gesture navigation
    const navBarHeightDp = 16;

    // Apply to CSS variables
    const root = document.documentElement;
    root.style.setProperty("--safe-area-inset-top", `${statusBarHeightDp}px`);
    root.style.setProperty("--safe-area-inset-bottom", `${navBarHeightDp}px`);
  }

  private async initializeIOS(): Promise<void> {
    // Configure status bar to overlay the webview
    await StatusBar.setOverlaysWebView({ overlay: true });
    await StatusBar.show();

    // Use StatusBar.getInfo() to get the status bar visibility info
    const _info = await StatusBar.getInfo();

    // Get bottom safe area from CSS env() - this is the home indicator height
    // We need to capture it before keyboard shows, as env() changes when keyboard appears
    const computedStyle = getComputedStyle(document.documentElement);
    const _envBottomInset = computedStyle.getPropertyValue("--safe-area-inset-bottom").trim();

    // Parse the env() value - it should be like "env(safe-area-inset-bottom, 0px)"
    // We need to get the actual computed value
    const tempDiv = document.createElement("div");
    tempDiv.style.height = "env(safe-area-inset-bottom, 0px)";
    document.body.appendChild(tempDiv);
    const bottomInset = Number.parseInt(getComputedStyle(tempDiv).height, 10) || 0;
    document.body.removeChild(tempDiv);

    // Apply to CSS variables
    // Note: StatusBarInfo does not expose height; read from env() instead
    const root = document.documentElement;
    const topTempDiv = document.createElement("div");
    topTempDiv.style.height = "env(safe-area-inset-top, 44px)";
    document.body.appendChild(topTempDiv);
    const topInset = Number.parseInt(getComputedStyle(topTempDiv).height, 10) || 44;
    document.body.removeChild(topTempDiv);
    root.style.setProperty("--safe-area-inset-top", `${topInset}px`);
    root.style.setProperty("--safe-area-inset-bottom", `${bottomInset}px`);
  }
}
